import { supabase, isInvalidRefreshTokenError } from '@/lib/supabase/client';
import { setSessionExpired } from '@/lib/sessionExpiryState';

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
// JWT / Supabase-style token — three base64url segments. This is the shape of
// every access / refresh / invite / share token that can end up inside an error
// string (e.g. a failed fetch that echoes the signed URL it was calling).
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
// Any remaining query string on an absolute URL (share/invite tokens, user
// input, signed-path params). The path is kept, the query is replaced.
const URL_QUERY_RE = /(\bhttps?:\/\/[^\s?#]+)\?[^\s#]*/gi;
// A token-shaped query parameter (?token=, access_token=, code=, apikey=…).
const TOKEN_PARAM_RE = /([?&][A-Za-z0-9_]*(?:token|secret|passwd|password|apikey|api_key|auth|signature|sig|code|key)[A-Za-z0-9_]*=)[^&#\s]*/gi;
// A long opaque token (hex or base64url) not otherwise identified above.
const LONG_TOKEN_RE = /\b[A-Za-z0-9_-]{32,}\b/g;
const MAX_MESSAGE_LENGTH = 2000;

/**
 * Strips personally identifiable information before it is persisted, so an
 * exception string that happens to leak an email, a record id, a token, or a
 * URL query string never stores user data in error_logs. Emails and UUIDs are
 * always masked; token-shaped strings (JWT, ?token=…, long opaque tokens) and
 * any URL query string are masked as well.
 */
export function maskPii(text: string): string {
  if (!text) return text;
  return text
    .replace(EMAIL_RE, '[email]')
    .replace(UUID_RE, '[uuid]')
    .replace(JWT_RE, '[token]')
    .replace(URL_QUERY_RE, '$1?[redacted]')
    .replace(TOKEN_PARAM_RE, '$1[redacted]')
    .replace(LONG_TOKEN_RE, '[token]')
    .slice(0, MAX_MESSAGE_LENGTH);
}

function detectBrowser(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  const ua = navigator.userAgent || '';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('SamsungBrowser/')) return 'Samsung Internet';
  if (ua.includes('Chrome/')) return 'Chrome';
  if (ua.includes('Safari/')) return 'Safari';
  return 'Other';
}

// Record only the pathname — the query string can contain share/invite tokens
// and other user input, so it is intentionally dropped.
function currentPath(): string {
  if (typeof window === 'undefined') return '';
  return window.location.pathname;
}

async function logError(path: string, message: string): Promise<void> {
  try {
    let isAuthenticated = false;
    try {
      const { data } = await supabase.auth.getSession();
      isAuthenticated = Boolean(data.session);
    } catch {
      isAuthenticated = false;
    }

    await supabase.from('error_logs').insert({
      path,
      message: maskPii(message),
      browser: detectBrowser(),
      is_authenticated: isAuthenticated,
    });
  } catch {
    // Error logging must never throw or recurse.
  }
}

/**
 * Records a non-fatal operational failure (e.g. an email that failed to send)
 * to the central error_logs table. Use this for paths that would otherwise
 * leave no trace — a dropped email, a failed resend, etc. — so the fact that
 * something failed is never invisible. The message is PII-masked before insert.
 */
export function logErrorToTable(path: string, message: string): void {
  void logError(path, message);
}

/** Record a caught failure (`catch (err)`) as "<what> failed: <message>". */
export function logCaught(path: string, what: string, err: unknown): void {
  const msg = err instanceof Error ? err.message : (err as { message?: string } | null)?.message ?? String(err);
  logErrorToTable(path, `${what} failed: ${msg}`);
}

// ---------------------------------------------------------------------------
// 1096 — Global error de-duplication
//
// The global error screen (GlobalErrorBoundary, 1145) and the window-level
// `error` / `unhandledrejection` listeners all funnel into the SAME
// error_logs sink. A screen that re-renders into the same failure, or a
// rejection that re-fires on every retry, would otherwise flood error_logs with
// identical rows. Within a 60s window only the FIRST occurrence of an identical
// (path, message) pair is recorded; repeats are skipped. The on-screen notice is
// untouched — only the logging side is suppressed.
// ---------------------------------------------------------------------------
const GLOBAL_ERROR_DEDUP_MS = 60_000;
const MAX_DEDUP_KEYS = 50;
const recentGlobalErrors = new Map<string, number>();

function shouldRecordGlobalError(path: string, message: string): boolean {
  const now = Date.now();
  const key = `${path}\u0000${message}`;
  const last = recentGlobalErrors.get(key);
  if (last != null && now - last < GLOBAL_ERROR_DEDUP_MS) {
    return false;
  }
  recentGlobalErrors.set(key, now);
  // Opportunistically evict stale keys so the map can never grow unbounded —
  // and so a genuine repeat later than the window is logged again.
  if (recentGlobalErrors.size > MAX_DEDUP_KEYS) {
    for (const [k, ts] of recentGlobalErrors) {
      if (now - ts >= GLOBAL_ERROR_DEDUP_MS) recentGlobalErrors.delete(k);
    }
  }
  return true;
}

/**
 * Global-error sink with de-duplication (1096). Use this from the app-level
 * error boundary and the window-level exception listeners so the SAME error
 * fired repeatedly within one minute is recorded exactly once. Falls through to
 * the normal `logError` path (PII-masked) otherwise.
 */
export function logGlobalError(path: string, message: string): void {
  if (!shouldRecordGlobalError(path, message)) return;
  void logError(path, message);
}

/**
 * Every distinct way a photo upload can fail. Stored verbatim in
 * `error_logs.reason` so a wave of failures can be grouped by cause on the
 * admin monitoring screen.
 */
export type PhotoUploadFailureReason =
  | 'heic'
  | 'saveFailed'
  | 'duplicate'
  | 'unsupportedType'
  | 'tooLarge'
  | 'videoTooLong'
  | 'encodeFailed';

export interface PhotoUploadFailureEntry {
  fileName: string;
  reason: PhotoUploadFailureReason;
}

export interface PhotoUploadFailureContext {
  /** Auth user id of the uploader (null for an anonymous session). */
  userId?: string | null;
  /** Trip the photos were being uploaded to, when known. */
  tripId?: number | null;
}

// The file NAME is the only user-supplied identifier we keep (per the privacy
// rule "file name only, never photo contents"). Bounded so a pathological name
// can't bloat the row.
const MAX_FILE_NAME_LENGTH = 200;

function safeFileName(name: string): string {
  const raw = name || 'unknown';
  let out = '';
  for (const ch of raw) {
    const code = ch.charCodeAt(0);
    // Drop control characters so a stray newline/tab can't corrupt the row.
    if (code >= 32 && code !== 127) out += ch;
  }
  return out.slice(0, MAX_FILE_NAME_LENGTH);
}

/**
 * Records failed photo uploads — ONE row per failed file — to `error_logs`.
 *
 * This is the single server-side log for EVERY upload failure type (HEIC not
 * supported, storage/DB save failure, duplicate, oversize video, …). Failures
 * that previously lived only in the uploader's browser (localStorage) are now
 * visible to admins on any device. Each row carries the failure time (the row's
 * `created_at`), the uploader's user id, the target trip id, the file name and
 * the reason.
 *
 * Privacy: exactly the file NAME is kept — never the photo contents, storage
 * path, caption or coordinates. The `message` is additionally PII-masked.
 */
export function logPhotoUploadFailure(
  entries: PhotoUploadFailureEntry[],
  context: PhotoUploadFailureContext = {},
): void {
  const named = entries.filter((e) => e && typeof e.fileName === 'string' && e.fileName.length > 0);
  if (named.length === 0) return;

  void (async () => {
    try {
      let isAuthenticated = false;
      try {
        const { data } = await supabase.auth.getSession();
        isAuthenticated = Boolean(data.session);
      } catch {
        isAuthenticated = false;
      }

      const rows = named.map((e) => ({
        path: 'photo-upload',
        message: maskPii(`Photo upload failed (${e.reason}): ${e.fileName}`),
        browser: detectBrowser(),
        is_authenticated: isAuthenticated,
        user_id: context.userId ?? null,
        trip_id: context.tripId ?? null,
        file_name: safeFileName(e.fileName),
        reason: e.reason,
      }));

      await supabase.from('error_logs').insert(rows);
    } catch {
      // Error logging must never throw or recurse.
    }
  })();
}

/**
 * Records committed photos whose storage object could not be confirmed in the
 * background object audit (queue 582).
 *
 * Privacy (582 supplement): stores ONLY the photo row id + the reason. The file
 * name, file contents, storage path and any signed URL are deliberately NOT
 * recorded. Because there is no dedicated photo-id column, the id goes into
 * `message` (unmasked — it is a bare record id, never user text) and the reason
 * into `reason`; `file_name` is intentionally left NULL.
 */
export function logMissingPhotoObjects(
  ids: (string | number | null | undefined)[],
  reason: PhotoUploadFailureReason = 'saveFailed',
  context: PhotoUploadFailureContext = {},
): void {
  const clean = ids.filter(
    (id): id is string | number => id != null && String(id).length > 0,
  );
  if (clean.length === 0) return;

  void (async () => {
    try {
      let isAuthenticated = false;
      try {
        const { data } = await supabase.auth.getSession();
        isAuthenticated = Boolean(data.session);
      } catch {
        isAuthenticated = false;
      }

      // id + reason ONLY — no file name, no path, no signed URL, no contents.
      const rows = clean.map((id) => ({
        path: 'photo-upload',
        message: `Photo object missing (${reason}): id=${id}`,
        browser: detectBrowser(),
        is_authenticated: isAuthenticated,
        user_id: context.userId ?? null,
        trip_id: context.tripId ?? null,
        reason,
      }));

      await supabase.from('error_logs').insert(rows);
    } catch {
      // Error logging must never throw or recurse.
    }
  })();
}

/**
 * The PDF pipeline stage a failure was attributed to (queue 741): the photo
 * image pipeline, page rendering, or the final document save. `unknown` is used
 * only when the failure happened outside the builder's tracked phases.
 */
export type AlbumPdfFailureStage = 'image' | 'render' | 'save' | 'unknown';

// 'prepare' = before the first photo is fetched (fonts, signing, video QR links).
const ALBUM_PDF_STAGES: ReadonlySet<string> = new Set(['prepare', 'image', 'render', 'save']);

/**
 * The ONLY error-code tokens allowed into `error_logs` for a PDF failure
 * (queue 748). Anything not in this fixed set — and not a bare numeric
 * HTTP/Postgres code — is replaced by the single constant `other`.
 */
export const ALBUM_PDF_ERROR_CODE_NAMES: readonly string[] = [
  'network',
  'memory',
  'timeout',
  'quota',
  'abort',
  'font',
  'decode',
  'unknown',
  // Built-in JS / DOM error names (see classifyAlbumError's fallback).
  'typeerror',
  'rangeerror',
  'referenceerror',
  'syntaxerror',
  'encodingerror',
  'invalidstateerror',
  'securityerror',
  'notreadableerror',
  'notsupportederror',
  'quotaexceedederror',
  'dataerror',
  'operationerror',
];

const ALBUM_PDF_ERROR_CODE_NAME_SET: ReadonlySet<string> = new Set(ALBUM_PDF_ERROR_CODE_NAMES);

// Bare HTTP status / Postgres SQLSTATE numeric codes are safe by construction.
const NUMERIC_ERROR_CODE_RE = /^\d{1,6}$/;

/**
 * Allowlist classifier for an album-PDF error code (queue 748). It NEVER keeps
 * free text. Only
 *   (1) a bare numeric code (e.g. `413`, `42501`), or
 *   (2) one of the fixed names defined above,
 * is ever returned. Every other value — a JWT body (`eyJ…`), a URL carrying a
 * `token=` query, an email, a signed path, a scheme/`@` fragment — collapses to
 * the single constant `other`. An empty/absent code becomes `unknown`.
 *
 * The previous implementation stripped only non-alphanumerics and kept the
 * rest, so `https://x.supabase.co/o?token=eyJ.secret@a.com` leaked
 * `httpsxsupabasecootokeneyJsecretacom` into the row. The allowlist removes
 * that whole class of leak: no raw string is ever echoed back.
 */
export function classifyAlbumPdfErrorCode(code: unknown): string {
  const raw = String(code ?? '').trim();
  if (raw.length === 0) return 'unknown';
  if (NUMERIC_ERROR_CODE_RE.test(raw)) return raw;
  const lower = raw.toLowerCase();
  if (ALBUM_PDF_ERROR_CODE_NAME_SET.has(lower)) return lower;
  return 'other';
}

/**
 * Build the exact `error_logs` row for one album-PDF failure — PURE, no side
 * effects. Kept separate from the insert so a verification pass can inspect the
 * recorded arguments WITHOUT writing any row (queue 741).
 *
 * Only four facts are ever recorded: the pipeline stage (also mirrored into the
 * `reason` column), the album page count, an ALLOWLISTED error code (a bare
 * numeric code or a fixed name, otherwise `other`), and the browser kind. There
 * is deliberately NO user id, trip id, file name, photo URL, signed token,
 * email or name — the row carries zero personal data.
 */
export function buildAlbumPdfFailureRow(input: {
  stage: string;
  pageCount: number;
  errorCode?: string;
}): {
  path: string;
  message: string;
  browser: string;
  is_authenticated: boolean;
  reason: string;
} {
  const stage = ALBUM_PDF_STAGES.has(input.stage) ? input.stage : 'unknown';
  const pages = Number.isFinite(input.pageCount) ? Math.max(0, Math.round(input.pageCount)) : 0;
  const code = classifyAlbumPdfErrorCode(input.errorCode);
  return {
    path: 'album-pdf',
    message: `PDF build failed (stage=${stage}, pages=${pages}, code=${code})`,
    browser: detectBrowser(),
    is_authenticated: false,
    reason: stage,
  };
}

/**
 * Records ONE row per album-PDF build failure so an operator can see whether
 * PDF generation is breaking (and at which stage) without any user report.
 * Goes through the SAME `error_logs` table as every other error path, so there
 * is no new table, policy or trigger. Fire-and-forget: it never throws, never
 * recurses, and a logging failure cannot affect the user's screen (the caller
 * has already shown its own error notice by the time this runs).
 */
export function logAlbumPdfFailure(input: {
  stage: string;
  pageCount: number;
  errorCode?: string;
}): void {
  void (async () => {
    try {
      let isAuthenticated = false;
      try {
        const { data } = await supabase.auth.getSession();
        isAuthenticated = Boolean(data.session);
      } catch {
        isAuthenticated = false;
      }

      const row = buildAlbumPdfFailureRow(input);
      await supabase.from('error_logs').insert({ ...row, is_authenticated: isAuthenticated });
    } catch {
      // Logging must never throw or recurse.
    }
  })();
}

// ---------------------------------------------------------------------------
// Console-error forwarding. Many caught failures across the app are reported
// only with console.error, which nobody sees in production. In production
// builds those calls are also recorded in error_logs (PII-masked, deduped per
// minute like global errors, and capped per page session so a loop can't flood
// the table). console.warn is not forwarded — it marks expected fallbacks.
// ---------------------------------------------------------------------------
const CONSOLE_ERROR_SESSION_CAP = 50;
let consoleErrorsForwarded = 0;

function describeConsoleArg(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === 'string') return arg;
  if (arg && typeof arg === 'object' && typeof (arg as { message?: unknown }).message === 'string') {
    return (arg as { message: string }).message;
  }
  try {
    return JSON.stringify(arg)?.slice(0, 300) ?? String(arg);
  } catch {
    return String(arg);
  }
}

export function forwardConsoleError(args: unknown[]): void {
  if (consoleErrorsForwarded >= CONSOLE_ERROR_SESSION_CAP) return;
  const message = `[console] ${args.map(describeConsoleArg).join(' ')}`;
  if (!shouldRecordGlobalError(currentPath(), message)) return;
  consoleErrorsForwarded += 1;
  void logError(currentPath(), message);
}

function installConsoleErrorForwarding(): void {
  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    original(...args);
    try {
      forwardConsoleError(args);
    } catch {
      // Forwarding must never break the original console call.
    }
  };
}

/**
 * Attaches global `error` / `unhandledrejection` listeners so uncaught
 * exceptions anywhere in the app are reported to the error_logs table, and
 * (production only) forwards console.error calls there too.
 */
export function initGlobalErrorLogging(): void {
  if (typeof window === 'undefined') return;

  if (import.meta.env.PROD) installConsoleErrorForwarding();

  window.addEventListener('error', (event) => {
    const message =
      event.message || (event.error ? String(event.error) : 'Unknown error');
    logGlobalError(currentPath(), message);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    // A revoked / already-used / unknown refresh token makes supabase-js reject
    // its internal token-refresh request. That is a NORMAL "sign in again"
    // condition, NOT an app bug: route it into the existing session-expiry UX
    // and keep it OUT of error_logs. preventDefault() also stops the browser
    // from treating it as an uncaught error, so no error report is raised for
    // something that is expected.
    if (isInvalidRefreshTokenError(reason)) {
      event.preventDefault();
      setSessionExpired(true);
      window.dispatchEvent(new CustomEvent('elkaway:session-expired'));
      return;
    }
    const message =
      reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason);
    logGlobalError(currentPath(), message);
  });
}